import { Component, OnInit, Input, Output, EventEmitter,  ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UsgService } from '../usg.service';
import { PathologyService } from '../pathology.service';
import { LoginService } from '../login.service';

@Component({
    selector: 'app-messenger-report',
    templateUrl: './messenger-report.component.html',
    styleUrls: ['./messenger-report.component.scss'],
    standalone: false
})
export class MessengerReportComponent implements OnInit, AfterViewChecked {
  private signaturePadInitialized = false;
  @ViewChild('signaturePad') signaturePadRef: ElementRef<HTMLCanvasElement>;
  
  messengerName: string = '';
  messengerSignature: string = '';
  signaturePad: HTMLCanvasElement | null = null;
  signaturePadCtx: CanvasRenderingContext2D | null = null;
  drawing: boolean = false;
  pathologies: any = [];
  groupedPathologies: any[] = [];
  @Input('lab') lab: string;
  @Input('reportID') inventoryId: number;
  @Output('closed') closed = new EventEmitter<Blob | void>();
  currentDate: Date = new Date();
  @Input('signed') signed: boolean;
  fromRoute: boolean = false;
  nurseSigningDate: Date | null = null;
  secretarySigningDate: Date | null = null;
  messengerSignedDate: Date | null = null;
  gastroct = 0;
  colonoct = 0;
  signing: boolean = false;

  constructor(
    private gastroService: UsgService,
    public loginService: LoginService,
    private pathologyService: PathologyService,
    private route: ActivatedRoute
  ) { }

  

  ngOnInit(): void {
    this.setupRouteParamsAndData();
  }

  ngAfterViewChecked(): void {
    if (!this.signaturePadInitialized && this.signaturePadRef && this.signaturePadRef.nativeElement) {
      this.initSignaturePad();
      this.signaturePadInitialized = true;
    }
  }

  // If lab or inventoryId are not set via @Input, get them from route params
  private setupRouteParamsAndData(): void {
    if (!this.lab) {
      this.lab = this.route.snapshot.params['lab'];
    }
    let code = null;
    if (!this.inventoryId) {
      const param = this.route.snapshot.params['reportid'];
      code = this.route.snapshot.params['code'];
      if (param !== undefined) {
        this.inventoryId = +param;
        this.fromRoute = true;
      }
    }
    
    this.pathologyService.getInventoryDetails(this.inventoryId, code).subscribe((data: any) => {
      if(data){
        if(data.MessengerName) {
          this.messengerName = data.MessengerName;
        }
        if(data.Lab) {
          this.lab = data.Lab;
        }
        if(data.NurseSigned) {
          this.nurseSigningDate = new Date(data.NurseSigned);
          if(this.loginService.userType == 'nurse'){
            this.signed = true;
          }
        }
        if(data.SecretarySigned) {
          this.secretarySigningDate = new Date(data.SecretarySigned);
        }
        if(data.MessengerDate) {
          this.messengerSignedDate = new Date(data.MessengerDate);
          this.messengerName = data.LabMessenger;
          this.signed = true;
        }
        this.getPathologies();
      }
      

    });
    
  }

  getDisplayId(id: any): string {
    if (Array.isArray(id)) {
      return id[0];
    }
    return id;
  }

  initSignaturePad(): void {
    if (!this.signaturePadRef || !this.signaturePadRef.nativeElement) return;
    const canvas = this.signaturePadRef.nativeElement;
    this.signaturePad = canvas;
    this.signaturePadCtx = canvas.getContext('2d');
    // Mouse events
    canvas.addEventListener('mousedown', (event) => this.startDraw(event));
    canvas.addEventListener('mousemove', (event) => this.draw(event));
    canvas.addEventListener('mouseup', (event) => this.endDraw(event));
    canvas.addEventListener('mouseleave', (event) => this.endDraw(event));
    // Touch events
    canvas.addEventListener('touchstart', (event) => this.startDraw(event));
    canvas.addEventListener('touchmove', (event) => this.draw(event));
    canvas.addEventListener('touchend', (event) => this.endDraw(event));
  }

  startDraw(event: MouseEvent | TouchEvent): void {
    this.drawing = true;
    if (!this.signaturePadCtx) return;
    this.signaturePadCtx.strokeStyle = '#222';
    this.signaturePadCtx.lineWidth = 2;
    this.signaturePadCtx.beginPath();
    const pos = this.getPointerPos(event);
    this.signaturePadCtx.moveTo(pos.x, pos.y);
  }

  draw(event: MouseEvent | TouchEvent): void {
    if (!this.drawing || !this.signaturePadCtx) return;
    event.preventDefault();
    const pos = this.getPointerPos(event);
    this.signaturePadCtx.lineTo(pos.x, pos.y);
    this.signaturePadCtx.stroke();
    this.signaturePadCtx.beginPath();
    this.signaturePadCtx.moveTo(pos.x, pos.y);
    this.updateMessengerSignature();
  }

  endDraw(event: MouseEvent | TouchEvent): void {
    if (!this.drawing) return;
    this.drawing = false;
    this.updateMessengerSignature();
  }

  getPointerPos(event: MouseEvent | TouchEvent): { x: number; y: number } {
    let x = 0, y = 0;
    if (event instanceof MouseEvent) {
      x = event.offsetX;
      y = event.offsetY;
    } else if ('touches' in event && event.touches.length > 0) {
      const rect = (event.target as HTMLCanvasElement).getBoundingClientRect();
      x = event.touches[0].clientX - rect.left;
      y = event.touches[0].clientY - rect.top;
    }
    return { x, y };
  }

  updateMessengerSignature(): void {
    if (this.signaturePad) {
      this.messengerSignature = this.signaturePad.toDataURL();
    }
  }

  clearMessengerSignature(): void {
    if (this.signaturePadCtx && this.signaturePad) {
      this.signaturePadCtx.clearRect(0, 0, this.signaturePad.width, this.signaturePad.height);
      this.messengerSignature = '';
    }
  }

  getPathologies(){
    this.pathologyService.prepPathologies(this.inventoryId).subscribe((data: any[]) => {
      this.pathologies = data;
      this.groupedPathologies = this.groupByExamId(Array.isArray(data) && data.length > 0 ? data : []);
    });
  }

  groupByExamId(data: any[]): any[] {
    const groups: {[key: string]: any[]} = {};
    for(let path of data) {
      if(path.ExamType == 'Gastroscopy')
        this.gastroct++;
      if(path.ExamType == 'Colonoscopy')
        this.colonoct++;
      if (!groups[path.ExamID]) {
        groups[path.ExamID] = [];
      }
      groups[path.ExamID].push(path);
    }
    return Object.keys(groups).map(ExamID => ({
      ExamID,
      pathologies: groups[ExamID]
    }));
  }

    printPopupBody(): void {
      const popupBody = document.querySelector('.popup-body');
      if (!popupBody) return;
      const printContents = popupBody.innerHTML;
      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write('<html><head><title>Print</title>');
      // Optionally include styles
      const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
      styles.forEach(style => {
        printWindow.document.write(style.outerHTML);
      });
      printWindow.document.write('</head><body>');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }

    signDetails(pathologies: any[]): void {
      let pathids = [];
      for(let p of pathologies){
        pathids.push(p.AutoIndex);
      }
      this.pathologyService.signSampleDetails(this.inventoryId, pathids, this.loginService.userType, this.loginService.UID).subscribe((data) => {
        for(let i=0; i<this.groupedPathologies.length; i++){
          if(this.groupedPathologies[i].ExamID == pathologies[0].ExamID){
            if(this.loginService.userType == 'nurse')
              this.groupedPathologies[i].pathologies[0].NurseSignature = this.loginService.getUserName();
            if(this.loginService.userType == 'secretary')
              this.groupedPathologies[i].pathologies[0].SecretarySignature = this.loginService.getUserName();
            }
          }
      });
    }

    nurseSigned(): boolean {//return true is everyhting is signed by nurse
      for(let group of this.groupedPathologies) {
        if(!group.pathologies[0].NurseSignature) {
          return false;
        }
      }
      return true;
    }

    secretarySigned(): boolean {//return true is everyhting is signed by secretary
      for(let group of this.groupedPathologies) {
        if(!group.pathologies[0].SecretarySignature) {
          return false;
        }
      }
      return true;
    }

    signInventory(){
      this.pathologyService.signInventory(this.inventoryId, this.loginService.UID, this.loginService.userType).subscribe((data) => {
        this.signed = true;
        if(this.loginService.userType == 'nurse'){
          this.closed.emit();
        }
        if(this.loginService.userType == 'secretary'){
          this.secretarySigningDate = new Date();
        }
        
      });
    }

    messengerSign(): void {
      // Send messengerName and messengerSignature (base64 image)
      if (!this.messengerName || !this.messengerSignature) return;
      this.signing = true;
      this.pathologyService.sendMessengerSignature(this.inventoryId, this.messengerName, this.messengerSignature).subscribe((pdfBlob) => {
        // Emit the PDF blob to parent
        this.closed.emit(pdfBlob);
      });
    }

    getLabName(): string {
      switch(this.lab) {
        case 'patholab': return 'פטולאב';
        case 'medica': return 'מדיקה';
        case 'megalab': return 'מגהלב';
        default: return this.lab;
      }
    }

}
